import type { ValidationResult } from "../types/validation";

// ============ BLOCKED EMAIL DOMAINS ============

const BLOCKED_EMAIL_DOMAINS: string[] = ["cpos.com", "cpospay.com"];

export interface ValidateEmailDomainOptions {
  environment?: string;
}

const STAGING_ALLOWED_DOMAIN = "cpos.com";

const ALLOWED_BYPASS_ENVIRONMENTS: string[] = ["staging", "development"];

/**
 * Validate email domain is not blocked
 * - @cpos.com is allowed in staging only (for internal testing per HUB-403)
 * - @cpospay.com remains blocked in all environments
 * - All blocked domains remain blocked in production
 */
export function validateEmailDomain(
  email: string | undefined | null,
  options?: ValidateEmailDomainOptions,
): ValidationResult {
  if (!email || typeof email !== "string") {
    return { isValid: true };
  }

  const trimmedEmail = email.trim().toLowerCase();
  const domain = trimmedEmail.split("@")[1];

  if (!domain) {
    return { isValid: true };
  }

  const isBlocked = BLOCKED_EMAIL_DOMAINS.some((blockedDomain) => {
    const lowerBlocked = blockedDomain.toLowerCase();
    return domain === lowerBlocked || domain.endsWith(`.${lowerBlocked}`);
  });

  if (!isBlocked) {
    return { isValid: true };
  }

  const environment =
    options?.environment || process.env.VITE_HUB_ENVIRONMENT || "production";
  const isCposDomain =
    domain === STAGING_ALLOWED_DOMAIN ||
    domain.endsWith(`.${STAGING_ALLOWED_DOMAIN}`);

  if (ALLOWED_BYPASS_ENVIRONMENTS.includes(environment) && isCposDomain) {
    return { isValid: true };
  }

  return {
    isValid: false,
    error:
      "This email domain is not allowed for registration. Please use a different email address.",
  };
}

// ============ BLOCKED BUSINESS NAMES ============

const BLOCKED_INDUSTRY_TERMS: string[] = [
  // Financial/Banking
  "bank",
  "banking",
  "credit union",
  "federal reserve",
  // Government
  "federal",
  "government",
  "irs",
  "treasury",
  "fbi",
  "cia",
  "nsa",
  "homeland security",
  "social security",
  // Airlines/Travel
  "airline",
  "airways",
  // Insurance
  "insurance",
];

const BLOCKED_COMPANY_NAMES: string[] = [
  // Internal/Brand Protection
  "cpos",
  "cpospay",

  // Tech Giants
  "google",
  "alphabet",
  "microsoft",
  "apple",
  "amazon",
  "meta",
  "facebook",
  "nvidia",
  "intel",
  "ibm",
  "cisco",
  "oracle",
  "salesforce",
  "adobe",
  "broadcom",
  "qualcomm",
  "dell",
  "hewlett packard",
  "vmware",
  "servicenow",
  "workday",
  "intuit",
  "autodesk",
  "splunk",
  "snowflake",
  "palantir",
  "databricks",
  "uber",
  "lyft",
  "airbnb",
  "doordash",
  "instacart",
  "twitter",
  "tiktok",
  "bytedance",
  "openai",
  "anthropic",
  "netflix",
  "spotify",
  "zoom",
  "slack",
  "dropbox",
  "box",
  "docusign",
  "crowdstrike",
  "palo alto",
  "fortinet",
  "zscaler",
  "okta",
  "twilio",
  "datadog",
  "cloudflare",
  "atlassian",
  "github",
  "gitlab",

  // Financial Services
  "jpmorgan",
  "jpmorgan chase",
  "chase bank",
  "chase manhattan",
  "wells fargo",
  "citigroup",
  "citibank",
  "goldman sachs",
  "morgan stanley",
  "american express",
  "amex",
  "visa",
  "mastercard",
  "capital one",
  "discover",
  "paypal",
  "stripe",
  "square",
  "block inc",
  "robinhood",
  "coinbase",
  "charles schwab",
  "fidelity",
  "blackrock",
  "vanguard",
  "state street",
  "prudential",
  "metlife",
  "aig",
  "allstate",
  "progressive",
  "geico",
  "liberty mutual",
  "nationwide",
  "usaa",
  "tiaa",
  "berkshire hathaway",
  "fannie mae",
  "freddie mac",

  // Retail & E-commerce
  "walmart",
  "costco",
  "target",
  "home depot",
  "lowes",
  "kroger",
  "albertsons",
  "publix",
  "walgreens",
  "cvs",
  "rite aid",
  "dollar general",
  "dollar tree",
  "tjx",
  "ross stores",
  "ross dress for less",
  "nordstrom",
  "macys",
  "kohls",
  "jcpenney",
  "sears",
  "best buy",
  "gamestop",
  "wayfair",
  "etsy",
  "ebay",
  "shopify",
  "alibaba",
  "aliexpress",
  "wish",

  // Healthcare & Pharma
  "unitedhealth",
  "anthem",
  "elevance",
  "cigna",
  "humana",
  "centene",
  "aetna",
  "kaiser",
  "blue cross",
  "blue shield",
  "pfizer",
  "johnson & johnson",
  "merck",
  "abbvie",
  "eli lilly",
  "bristol-myers",
  "amgen",
  "gilead",
  "regeneron",
  "moderna",
  "biogen",
  "vertex",
  "illumina",
  "thermo fisher",
  "labcorp",
  "quest diagnostics",
  "hca healthcare",
  "tenet",
  "mckesson",
  "cencora",
  "cardinal health",

  // Consumer Brands & Food
  "coca-cola",
  "pepsi",
  "pepsico",
  "nestle",
  "unilever",
  "procter & gamble",
  "colgate",
  "kimberly-clark",
  "general mills",
  "kellogg",
  "kraft heinz",
  "mondelez",
  "mars",
  "hershey",
  "tyson",
  "hormel",
  "conagra",
  "mccormick",
  "starbucks",
  "mcdonalds",
  "mcdonald's",
  "burger king",
  "wendys",
  "wendy's",
  "chipotle",
  "dominos",
  "domino's",
  "papa johns",
  "papa john's",
  "yum brands",
  "subway",
  "dunkin",
  "krispy kreme",
  "chick-fil-a",
  "popeyes",
  "taco bell",
  "kfc",
  "pizza hut",

  // Automotive & Transportation
  "tesla",
  "ford motor",
  "ford motors",
  "ford automotive",
  "ford dealership",
  "ford dealer",
  "ford certified",
  "general motors",
  "chrysler",
  "toyota",
  "honda",
  "nissan",
  "hyundai",
  "kia",
  "bmw",
  "mercedes",
  "volkswagen",
  "audi",
  "porsche",
  "ferrari",
  "lamborghini",
  "hertz",
  "enterprise rent-a-car",
  "enterprise car rental",
  "enterprise rental",
  "avis",
  "united airlines",
  "delta airlines",
  "delta air lines",
  "american airlines",
  "southwest",
  "jetblue",
  "spirit airlines",
  "fedex",
  "ups",
  "usps",
  "dhl",

  // Energy & Utilities
  "exxon mobil",
  "exxonmobil",
  "chevron",
  "conocophillips",
  "marathon",
  "valero",
  "phillips 66",
  "shell",
  "duke energy",
  "dominion",
  "southern company",
  "nextera",
  "xcel energy",

  // Telecom & Media
  "at&t",
  "verizon",
  "t-mobile",
  "sprint",
  "comcast",
  "xfinity",
  "charter",
  "spectrum",
  "cox",
  "dish",
  "directv",
  "disney",
  "warner bros",
  "paramount",
  "hulu",
  "hbo",
  "showtime",
  "espn",
  "fox news",
  "fox sports",
  "fox broadcasting",
  "fox entertainment",
  "fox corporation",
  "nbc",
  "cbs",
  "cnn",
  "msnbc",
  "cnbc",
  "bloomberg",
  "reuters",
  "associated press",
  "new york times",
  "washington post",
  "wall street journal",

  // Apparel & Fashion
  "nike",
  "adidas",
  "under armour",
  "puma",
  "reebok",
  "lululemon",
  "gap",
  "old navy",
  "banana republic",
  "zara",
  "h&m",
  "forever 21",
  "victorias secret",
  "victoria's secret",
  "sephora",
  "ulta beauty",
  "estee lauder",
  "loreal",
  "l'oreal",
  "revlon",
  "coach",
  "michael kors",
  "kate spade",
  "gucci",
  "louis vuitton",
  "chanel",
  "prada",
  "burberry",
  "ralph lauren",
  "calvin klein",
  "tommy hilfiger",
  "levi's",
  "levis",

  // Industrial & Manufacturing
  "boeing",
  "lockheed martin",
  "raytheon",
  "northrop grumman",
  "general dynamics",
  "honeywell",
  "3m",
  "caterpillar",
  "deere",
  "john deere",
  "ge",
  "general electric",
  "siemens",
  "bosch",
  "philips",
  "panasonic",
  "sony",
  "samsung",
  "lg",
  "toshiba",
  "hitachi",
  "mitsubishi",

  // Entertainment & Gaming
  "mattel",
  "hasbro",
  "lego",
  "nintendo",
  "playstation",
  "xbox",
  "activision",
  "blizzard",
  "electronic arts",
  "take-two",
  "rockstar games",
  "epic games",
  "roblox",
  "riot games",
  "valve",
  "steam",
];

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeForComparison(str: string): string {
  return str.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
}

interface BlockedTermPattern {
  term: string;
  regex: RegExp;
}

/* eslint-disable security/detect-non-literal-regexp */
const INDUSTRY_TERM_PATTERNS: BlockedTermPattern[] = BLOCKED_INDUSTRY_TERMS.map(
  (term) => ({
    term,
    regex: new RegExp(`\\b${escapeRegex(term)}\\b`, "i"), // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
  }),
);

const COMPANY_NAME_PATTERNS: BlockedTermPattern[] = BLOCKED_COMPANY_NAMES.map(
  (term) => ({
    term,
    regex: new RegExp(`\\b${escapeRegex(term)}\\b`, "i"), // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
  }),
);
/* eslint-enable security/detect-non-literal-regexp */

/**
 * Validate business name against fraud patterns
 */
export function validateBusinessNameFraud(
  businessName: string | undefined | null,
): ValidationResult {
  if (!businessName || typeof businessName !== "string") {
    return { isValid: true };
  }

  const trimmedName = businessName.trim();

  if (trimmedName.length === 0) {
    return { isValid: true };
  }

  const normalizedName = normalizeForComparison(trimmedName);

  for (const { term, regex } of INDUSTRY_TERM_PATTERNS) {
    if (regex.test(normalizedName)) {
      return {
        isValid: false,
        error: `Business names containing "${term}" are not allowed. If you believe this is a legitimate business, please contact support.`,
      };
    }
  }

  for (const { term, regex } of COMPANY_NAME_PATTERNS) {
    if (regex.test(normalizedName)) {
      return {
        isValid: false,
        error: `Business names containing "${term}" are not allowed. If you believe this is a legitimate business, please contact support.`,
      };
    }
  }

  return { isValid: true };
}

export const _testExports = {
  BLOCKED_EMAIL_DOMAINS,
  BLOCKED_INDUSTRY_TERMS,
  BLOCKED_COMPANY_NAMES,
};
